/**
 * @fileoverview Multi unidades v2 — visual SAAS premium.
 *
 * Filosofia: cockpit executivo. Hierarquia em tiers S/A/B/C com
 * NOW separado de histórico. Accent monocromático #5B7CFF. Cores
 * semantic (healthy/watch/breach) reservadas pra status SLA. Dark
 * glass como ar visual. Densidade alta mas legível.
 *
 * Endpoint único: GET /dashboard/multi-unidades-aggregate (cache 30s).
 */

import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback
} from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  RefreshCw,
  Download,
  Maximize2,
  Search,
  Eye,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Activity,
  MessageSquare,
  X as XIcon
} from "lucide-react";
import Dialog from "@material-ui/core/Dialog";
import { AuthContext } from "../../../context/Auth/AuthContext";
import api from "../../../services/api";
import { getMUv2Tokens, slaStatus } from "./theme";
import Sparkline from "./components/Sparkline";
import StatusBadge from "./components/StatusBadge";
import DeltaPill from "./components/DeltaPill";
import GlassTooltip from "./components/GlassTooltip";
import BarInCell from "./components/BarInCell";
import LivePulseDot from "./components/LivePulseDot";

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────
const pad2 = n => String(n).padStart(2, "0");

const secToHms = sec => {
  if (sec == null || sec <= 0) return "—";
  const s = Math.round(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return h + ":" + pad2(m) + ":" + pad2(r);
  return pad2(m) + ":" + pad2(r);
};

const secToShort = sec => {
  if (sec == null || sec <= 0) return "—";
  const s = Math.round(sec);
  if (s < 60) return s + "s";
  const m = Math.floor(s / 60);
  if (m < 60) return m + "min";
  const h = Math.floor(m / 60);
  const r = m - h * 60;
  return r === 0 ? h + "h" : h + "h" + r + "min";
};

const fmtNumberPtBR = n => {
  if (n == null) return "—";
  return Number(n).toLocaleString("pt-BR");
};

const formatARTMinutes = m => {
  if (m == null || Number.isNaN(m)) return "—";
  if (m < 1) return Math.round(m * 60) + "s";
  if (m < 60) return Math.round(m * 10) / 10 + "min";
  const h = Math.floor(m / 60);
  const rest = Math.round(m - h * 60);
  return rest === 0 ? h + "h" : h + "h" + rest + "min";
};

const relTime = iso => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const elapsed = Math.max(0, Date.now() - then);
  const s = Math.floor(elapsed / 1000);
  if (s < 5) return "agora";
  if (s < 60) return s + "s";
  const m = Math.floor(s / 60);
  if (m < 60) return m + "min";
  const h = Math.floor(m / 60);
  if (h < 24) return h + "h";
  return Math.floor(h / 24) + "d";
};

// Gera array sintético de 12 pontos pra sparkline quando não tem
// histórico real ainda (visual placeholder coerente).
const placeholderSpark = (target, variance = 0.3) => {
  const arr = [];
  for (let i = 0; i < 12; i++) {
    const wave = Math.sin((i / 12) * Math.PI * 2) * variance;
    arr.push(Math.max(0, Math.round(target * (1 - variance / 2 + wave))));
  }
  arr[arr.length - 1] = target;
  return arr;
};

// ─────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────
const useStyles = makeStyles(theme => {
  const t = getMUv2Tokens(theme);
  return {
    root: {
      minHeight: "calc(100vh - 56px)",
      background: t.pageBg,
      fontFamily: "'Montserrat', sans-serif",
      color: t.textPrimary,
      paddingBottom: 48
    },

    // Z0 Top Bar
    topBar: {
      position: "sticky",
      top: 0,
      height: 56,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 24px",
      background: t.isDark ? "rgba(10,10,10,0.72)" : "rgba(248,250,252,0.85)",
      backdropFilter: t.backdropFilter,
      WebkitBackdropFilter: t.backdropFilter,
      borderBottom: "1px solid " + t.cardBorder,
      zIndex: 50
    },
    breadcrumb: {
      fontSize: 13,
      fontWeight: 600,
      color: t.textSecondary,
      letterSpacing: "0.01em",
      "& span": { color: t.textPrimary }
    },
    liveCluster: {
      display: "flex",
      gap: 8,
      alignItems: "center",
      fontSize: 12,
      color: t.textMuted,
      fontWeight: 500
    },
    liveLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: t.healthyText
    },
    actionsCluster: { display: "flex", gap: 4 },
    actionBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 10px",
      borderRadius: 8,
      background: "transparent",
      border: "1px solid transparent",
      color: t.textSecondary,
      cursor: "pointer",
      transition: "all 160ms ease",
      fontSize: 12,
      fontWeight: 600,
      fontFamily: "inherit",
      "&:hover": {
        background: t.surfaceBg,
        borderColor: t.cardBorder,
        color: t.textPrimary
      }
    },

    // Content wrap
    contentWrap: { maxWidth: 1440, margin: "0 auto", padding: "24px" },

    // Z1 Hero Strip
    heroStrip: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 16,
      marginBottom: 24,
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2, 1fr)" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" }
    },
    heroCard: {
      padding: 24,
      background: t.cardBgStrong,
      border: "1px solid " + t.cardBorder,
      borderRadius: 12,
      minHeight: 168,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      position: "relative",
      transition: "all 220ms ease",
      overflow: "hidden",
      "&:hover": {
        transform: "translateY(-1px)",
        borderColor: t.cardBorderHover,
        boxShadow: t.cardShadowHover
      }
    },
    heroCardHealthy: { borderLeft: "3px solid " + t.accent },
    heroCardWatch: { borderLeft: "3px solid " + t.watch },
    heroCardBreach: { borderLeft: "3px solid " + t.breach },
    heroHeaderRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start"
    },
    heroLabel: {
      textTransform: "uppercase",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      color: t.textMuted
    },
    heroValueRow: {
      display: "flex",
      alignItems: "baseline",
      gap: 8,
      marginTop: 8
    },
    heroValue: {
      fontSize: 38,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: t.textPrimary,
      lineHeight: 1,
      letterSpacing: "-0.02em"
    },
    heroValueBreach: { color: t.breachText },
    heroValueWatch: { color: t.watchText },
    heroFooterRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      marginTop: 12
    },
    heroSparkSlot: { width: 96, height: 28, opacity: 0.85 },

    // Top offenders (card 4)
    topOffList: { display: "flex", flexDirection: "column", gap: 8 },
    topOffItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "4px 0",
      borderBottom: "1px dashed " + t.cardBorder,
      fontSize: 12,
      "&:last-child": { borderBottom: "none" }
    },
    topOffName: {
      color: t.textPrimary,
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: 140
    },
    topOffArt: {
      fontVariantNumeric: "tabular-nums",
      fontWeight: 700,
      fontSize: 12
    },

    // Z2 Filter Bar
    filterBar: {
      position: "sticky",
      top: 56,
      padding: "12px 0",
      background: t.pageBg,
      borderBottom: "1px solid " + t.cardBorder,
      zIndex: 40,
      display: "flex",
      gap: 12,
      alignItems: "center",
      marginBottom: 16,
      flexWrap: "wrap"
    },
    periodSegmented: {
      display: "inline-flex",
      background: t.surfaceBg,
      border: "1px solid " + t.cardBorder,
      borderRadius: 8,
      padding: 3
    },
    periodBtn: {
      padding: "6px 14px",
      borderRadius: 6,
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 600,
      color: t.textMuted,
      background: "transparent",
      border: "none",
      fontFamily: "inherit",
      transition: "all 160ms ease",
      "&:hover": { color: t.textPrimary }
    },
    periodBtnActive: {
      background: t.accentSoft,
      color: t.accent
    },
    searchWrap: { position: "relative", marginLeft: "auto" },
    searchInput: {
      width: 240,
      padding: "8px 12px 8px 34px",
      background: t.surfaceBg,
      border: "1px solid " + t.cardBorder,
      borderRadius: 8,
      color: t.textPrimary,
      fontSize: 12,
      fontFamily: "inherit",
      transition: "all 160ms ease",
      "&::placeholder": { color: t.textMuted },
      "&:focus": {
        outline: "none",
        borderColor: t.accentBorder,
        background: t.cardBg
      }
    },
    searchIcon: {
      position: "absolute",
      left: 10,
      top: "50%",
      transform: "translateY(-50%)",
      color: t.textMuted,
      pointerEvents: "none"
    },

    // Z3 Performance Table
    tableCard: {
      background: t.cardBg,
      border: "1px solid " + t.cardBorder,
      borderRadius: 12,
      marginBottom: 24,
      overflow: "hidden"
    },
    tableHeader: {
      display: "grid",
      gridTemplateColumns:
        "20px minmax(170px,1fr) 96px 120px 96px 110px 110px 80px 40px",
      gap: 12,
      padding: "10px 16px",
      background: t.cardBgStrong,
      borderBottom: "1px solid " + t.cardBorder,
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: t.textMuted,
      alignItems: "center"
    },
    sortableHeader: {
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      userSelect: "none",
      "&:hover": { color: t.textPrimary }
    },
    tableRow: {
      display: "grid",
      gridTemplateColumns:
        "20px minmax(170px,1fr) 96px 120px 96px 110px 110px 80px 40px",
      gap: 12,
      padding: "10px 16px",
      borderBottom: "1px solid " + t.cardBorder,
      alignItems: "center",
      transition: "all 160ms ease",
      "&:hover": { background: t.accentSoft },
      "&:last-child": { borderBottom: "none" }
    },
    tableCellName: {
      fontSize: 13,
      fontWeight: 600,
      color: t.textPrimary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    },
    tableCellNum: {
      fontSize: 12,
      fontVariantNumeric: "tabular-nums",
      color: t.textSecondary,
      fontWeight: 500
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      display: "inline-block"
    },

    // Z4 Charts Strip
    chartsHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 12,
      marginTop: 8
    },
    chartsTitle: {
      fontSize: 14,
      fontWeight: 700,
      color: t.textPrimary,
      letterSpacing: "-0.01em"
    },
    chartsSub: {
      fontSize: 11,
      color: t.textMuted,
      fontWeight: 500,
      textTransform: "uppercase",
      letterSpacing: "0.08em"
    },
    chartsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 16,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" }
    },
    chartCard: {
      padding: "16px 18px",
      background: t.cardBg,
      border: "1px solid " + t.cardBorder,
      borderRadius: 12,
      minHeight: 200,
      display: "flex",
      flexDirection: "column",
      transition: "all 180ms ease",
      "&:hover": {
        borderColor: t.cardBorderHover,
        boxShadow: t.cardShadow
      }
    },
    chartHeaderRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 12,
      gap: 8
    },
    chartTitle: {
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: t.textMuted,
      fontWeight: 700
    },
    chartValue: {
      fontSize: 24,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: t.textPrimary,
      letterSpacing: "-0.02em"
    },
    chartBody: { flex: 1, minHeight: 120, position: "relative" },

    // States
    skeletonCard: {
      padding: 24,
      background: t.cardBg,
      border: "1px solid " + t.cardBorder,
      borderRadius: 12,
      minHeight: 168,
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(110deg, transparent 35%, " +
          t.accentSoft +
          " 50%, transparent 65%)",
        animation: "muv2-shimmer 1600ms ease-in-out infinite"
      }
    },
    "@global": {
      "@keyframes muv2-shimmer": {
        "0%": { transform: "translateX(-100%)" },
        "100%": { transform: "translateX(100%)" }
      }
    },
    emptyState: {
      padding: 48,
      textAlign: "center",
      color: t.textMuted,
      fontSize: 13,
      fontWeight: 500
    },
    errorCard: {
      padding: 16,
      background: t.breachBg,
      border: "1px solid " + t.breachBorder,
      borderRadius: 12,
      color: t.breachText,
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginBottom: 24
    },
    retryBtn: {
      background: t.breachBorder,
      color: "#fff",
      padding: "6px 12px",
      borderRadius: 6,
      border: "none",
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 700,
      fontFamily: "inherit",
      marginLeft: "auto",
      "&:hover": { background: t.breach }
    },

    // Spinning refresh
    refreshSpinning: { animation: "muv2-spin 800ms linear infinite" }
  };
});

// Inject spin keyframes (escape do "@global" pattern bug do MUI v4)
const GlobalSpinStyle = () => (
  <style>{`@keyframes muv2-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
);

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────
const MultiUnidadesV2 = ({ groupName: propGroupName }) => {
  const theme = useTheme();
  const tokens = getMUv2Tokens(theme);
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const groupName = propGroupName || user?.company?.groupName || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState("7d");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("tickets");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [detailRow, setDetailRow] = useState(null); // atendente selecionado pro modal
  const [, setNowTick] = useState(0);
  const PAGE_SIZE = 15;

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = { period };
    if (groupName) params.groupName = groupName;
    api
      .get("/dashboard/multi-unidades-aggregate", { params })
      .then(r => setData(r.data))
      .catch(e => {
        setError(
          e?.response?.data?.error ||
            e?.message ||
            "Erro ao carregar dados."
        );
      })
      .finally(() => setLoading(false));
  }, [groupName, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Tick a cada 15s pra refresh do relTime
  useEffect(() => {
    const id = setInterval(() => setNowTick(t => t + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  // Auto refresh a cada 60s (silencioso)
  useEffect(() => {
    const id = setInterval(() => fetchData(), 60_000);
    return () => clearInterval(id);
  }, [fetchData]);

  // ── Derived ──
  const tierS = data?.tierS;
  const performance = data?.performance || [];
  const charts = data?.charts;
  const artRanking = data?.artRanking || [];

  const filteredAttendants = useMemo(() => {
    let arr = performance;
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(a => (a.name || "").toLowerCase().includes(q));
    }
    arr = [...arr].sort((a, b) => {
      const av = a[sortBy] ?? 0;
      const bv = b[sortBy] ?? 0;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [performance, search, sortBy, sortDir]);

  // Reset pra pagina 1 sempre que filtro/sort muda
  useEffect(() => { setPage(1); }, [search, sortBy, sortDir, period]);

  const totalPages = Math.max(1, Math.ceil(filteredAttendants.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedAttendants = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredAttendants.slice(start, start + PAGE_SIZE);
  }, [filteredAttendants, safePage]);

  const maxTickets = useMemo(
    () => Math.max(1, ...performance.map(a => Number(a.tickets) || 0)),
    [performance]
  );

  const toggleSort = field => {
    if (sortBy === field) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortDir("desc");
    }
  };

  const renderSortIcon = field => {
    if (sortBy !== field) return null;
    return sortDir === "asc" ? <ChevronUp size={11} /> : <ChevronDown size={11} />;
  };

  // ── Hero values + status ──
  const ativosAgora = tierS?.ativosAgora ?? 0;
  const semResposta = tierS?.semResposta ?? 0;
  const semRespostaRate = tierS?.semRespostaRate ?? 0;
  const semRespostaStatus = semResposta === 0 ? "healthy" : semResposta < 5 ? "watch" : "breach";
  const artMin = tierS?.art?.avgMinutes ?? null;
  const artSec = artMin != null ? artMin * 60 : 0;
  const artStatus = slaStatus(artSec, { ok: 600, warn: 1200 }); // 10/20min

  // ── Render ──
  return (
    <div className={classes.root}>
      <GlobalSpinStyle />

      {/* ───── Z0 TOP BAR ───── */}
      <div className={classes.topBar}>
        <div className={classes.breadcrumb}>
          Multi unidades&nbsp;/&nbsp;<span>Operacional</span>
          {groupName && (
            <span style={{ color: tokens.textMuted, fontWeight: 500 }}>
              &nbsp;·&nbsp;{groupName}
            </span>
          )}
        </div>
        <div className={classes.liveCluster}>
          <LivePulseDot color={tokens.healthy} />
          <span className={classes.liveLabel}>Live</span>
          <span>·</span>
          <span>
            atualizado {relTime(data?.meta?.generatedAt)} atrás
            {data?.meta?.cacheHit && (
              <span style={{ color: tokens.textMuted, marginLeft: 6 }}>(cache)</span>
            )}
            {data?.meta?.wallTimeMs != null && (
              <span style={{ color: tokens.textMuted, marginLeft: 6 }}>
                · {data.meta.wallTimeMs}ms
              </span>
            )}
          </span>
        </div>
        <div className={classes.actionsCluster}>
          <GlassTooltip title="Atualizar agora" arrow placement="bottom">
            <button
              className={classes.actionBtn}
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw
                size={15}
                className={loading ? classes.refreshSpinning : ""}
              />
            </button>
          </GlassTooltip>
          <GlassTooltip title="Exportar CSV" arrow placement="bottom">
            <button className={classes.actionBtn}>
              <Download size={15} />
            </button>
          </GlassTooltip>
          <GlassTooltip title="Tela cheia" arrow placement="bottom">
            <button className={classes.actionBtn}>
              <Maximize2 size={15} />
            </button>
          </GlassTooltip>
        </div>
      </div>

      <div className={classes.contentWrap}>
        {/* ───── ERROR ───── */}
        {error && (
          <div className={classes.errorCard}>
            <AlertCircle size={18} />
            <span style={{ flex: 1 }}>{error}</span>
            <button className={classes.retryBtn} onClick={fetchData}>
              Tentar novamente
            </button>
          </div>
        )}

        {/* ───── Z1 HERO STRIP ───── */}
        <div className={classes.heroStrip}>
          {/* Loading skeleton */}
          {loading && !data
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={classes.skeletonCard} />
              ))
            : (
              <>
                {/* Card 1 — Ativos Agora */}
                <div
                  className={`${classes.heroCard} ${classes.heroCardHealthy}`}
                >
                  <div className={classes.heroHeaderRow}>
                    <span className={classes.heroLabel}>Ativos agora</span>
                    <LivePulseDot color={tokens.accent} size={7} />
                  </div>
                  <div className={classes.heroValueRow}>
                    <span className={classes.heroValue}>
                      {fmtNumberPtBR(ativosAgora)}
                    </span>
                  </div>
                  <div className={classes.heroFooterRow}>
                    <span style={{ fontSize: 11, color: tokens.textMuted }}>
                      últimas 24h
                    </span>
                    <div className={classes.heroSparkSlot}>
                      <Sparkline
                        data={placeholderSpark(ativosAgora || 50, 0.4)}
                        color={tokens.accent}
                        width={96}
                        height={28}
                        idSuffix="ativos"
                      />
                    </div>
                  </div>
                </div>

                {/* Card 2 — Sem Resposta */}
                <div
                  className={`${classes.heroCard} ${
                    semRespostaStatus === "breach"
                      ? classes.heroCardBreach
                      : semRespostaStatus === "watch"
                      ? classes.heroCardWatch
                      : classes.heroCardHealthy
                  }`}
                >
                  <div className={classes.heroHeaderRow}>
                    <span className={classes.heroLabel}>Sem resposta</span>
                    <StatusBadge status={semRespostaStatus} tokens={tokens} />
                  </div>
                  <div className={classes.heroValueRow}>
                    <span
                      className={`${classes.heroValue} ${
                        semRespostaStatus === "breach"
                          ? classes.heroValueBreach
                          : ""
                      }`}
                    >
                      {fmtNumberPtBR(semResposta)}
                    </span>
                    {semRespostaRate > 0 && (
                      <span
                        style={{
                          fontSize: 13,
                          color: tokens.textMuted,
                          fontWeight: 600
                        }}
                      >
                        {semRespostaRate}% dos ativos
                      </span>
                    )}
                  </div>
                  <div className={classes.heroFooterRow}>
                    <span style={{ fontSize: 11, color: tokens.textMuted }}>
                      +10min sem resposta
                    </span>
                    <div className={classes.heroSparkSlot}>
                      <Sparkline
                        data={placeholderSpark(semResposta || 3, 0.6)}
                        color={
                          semRespostaStatus === "breach"
                            ? tokens.breach
                            : tokens.accent
                        }
                        width={96}
                        height={28}
                        idSuffix="semresp"
                      />
                    </div>
                  </div>
                </div>

                {/* Card 3 — ART tempo real */}
                <div
                  className={`${classes.heroCard} ${
                    artStatus === "breach"
                      ? classes.heroCardBreach
                      : artStatus === "watch"
                      ? classes.heroCardWatch
                      : classes.heroCardHealthy
                  }`}
                >
                  <div className={classes.heroHeaderRow}>
                    <span className={classes.heroLabel}>ART rolling 24h</span>
                    <StatusBadge status={artStatus} tokens={tokens} />
                  </div>
                  <div className={classes.heroValueRow}>
                    <span
                      className={`${classes.heroValue} ${
                        artStatus === "breach"
                          ? classes.heroValueBreach
                          : artStatus === "watch"
                          ? classes.heroValueWatch
                          : ""
                      }`}
                    >
                      {formatARTMinutes(artMin)}
                    </span>
                  </div>
                  <div className={classes.heroFooterRow}>
                    <span style={{ fontSize: 11, color: tokens.textMuted }}>
                      {tierS?.art?.pairCount
                        ? fmtNumberPtBR(tierS.art.pairCount) + " pares"
                        : "sem dados"}
                    </span>
                    <div className={classes.heroSparkSlot}>
                      <Sparkline
                        data={placeholderSpark(artMin || 8, 0.35)}
                        color={
                          artStatus === "breach"
                            ? tokens.breach
                            : artStatus === "watch"
                            ? tokens.watch
                            : tokens.accent
                        }
                        width={96}
                        height={28}
                        idSuffix="art"
                      />
                    </div>
                  </div>
                </div>

                {/* Card 4 — Top Ofensores (ART) */}
                <div className={`${classes.heroCard} ${classes.heroCardWatch}`}>
                  <div className={classes.heroHeaderRow}>
                    <span className={classes.heroLabel}>
                      Top {Math.min(3, artRanking.length)} ofensores · ART
                      {artRanking.length > 0 && (
                        <span style={{ color: tokens.textMuted, fontWeight: 500 }}>
                          {" "}
                          / {artRanking.length} total
                        </span>
                      )}
                    </span>
                  </div>
                  <div className={classes.topOffList} style={{ marginTop: 12 }}>
                    {artRanking.length === 0 ? (
                      <span
                        style={{
                          color: tokens.textMuted,
                          fontSize: 12,
                          fontStyle: "italic"
                        }}
                      >
                        Sem dados disponíveis.
                      </span>
                    ) : (
                      artRanking.slice(-3).reverse().map((r, i) => (
                        <div key={i} className={classes.topOffItem}>
                          <span className={classes.topOffName}>{r.name}</span>
                          <span
                            className={classes.topOffArt}
                            style={{
                              color:
                                slaStatus(r.avgMinutes * 60, {
                                  ok: 600,
                                  warn: 1200
                                }) === "breach"
                                  ? tokens.breachText
                                  : slaStatus(r.avgMinutes * 60, {
                                      ok: 600,
                                      warn: 1200
                                    }) === "watch"
                                  ? tokens.watchText
                                  : tokens.healthyText
                            }}
                          >
                            {formatARTMinutes(r.avgMinutes)}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
        </div>

        {/* ───── Z2 FILTER BAR ───── */}
        <div className={classes.filterBar}>
          <div className={classes.periodSegmented}>
            {["hoje", "7d", "30d"].map(p => (
              <button
                key={p}
                className={`${classes.periodBtn} ${
                  period === p ? classes.periodBtnActive : ""
                }`}
                onClick={() => setPeriod(p)}
              >
                {p === "hoje" ? "Hoje" : p}
              </button>
            ))}
          </div>
          <div className={classes.searchWrap}>
            <Search size={14} className={classes.searchIcon} />
            <input
              className={classes.searchInput}
              placeholder="Buscar atendente..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* ───── Z3 PERFORMANCE TABLE ───── */}
        <div style={{ marginBottom: 8 }} className={classes.chartsHeader}>
          <span className={classes.chartsTitle}>Performance · atendentes</span>
          <span className={classes.chartsSub}>
            {search.trim()
              ? `${filteredAttendants.length} resultado(s) de ${performance.length} atendentes`
              : `${performance.length} atendentes ativos · ${pagedAttendants.length} nesta página`}
          </span>
        </div>
        <div className={classes.tableCard}>
          <div className={classes.tableHeader}>
            <span />
            <span
              className={classes.sortableHeader}
              onClick={() => toggleSort("name")}
            >
              Atendente {renderSortIcon("name")}
            </span>
            <span>Status</span>
            <span
              className={classes.sortableHeader}
              onClick={() => toggleSort("tickets")}
            >
              Atendimentos {renderSortIcon("tickets")}
            </span>
            <span
              className={classes.sortableHeader}
              onClick={() => toggleSort("messages")}
            >
              Mensagens {renderSortIcon("messages")}
            </span>
            <span
              className={classes.sortableHeader}
              onClick={() => toggleSort("avgSupportTime")}
            >
              ART atendente {renderSortIcon("avgSupportTime")}
            </span>
            <span
              className={classes.sortableHeader}
              onClick={() => toggleSort("avgWaitTime")}
            >
              Tempo espera {renderSortIcon("avgWaitTime")}
            </span>
            <span>Hoje</span>
            <span />
          </div>
          {loading && !data ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className={classes.tableRow}
                style={{ opacity: 0.4, pointerEvents: "none" }}
              >
                <span />
                <span
                  className={classes.tableCellName}
                  style={{ color: tokens.textMuted }}
                >
                  carregando…
                </span>
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
            ))
          ) : filteredAttendants.length === 0 ? (
            <div className={classes.emptyState}>
              Nenhum atendente encontrado nesse filtro.
            </div>
          ) : (
            pagedAttendants.map((att, i) => {
              // Threshold de ART pra atendente: <5min ok / <10min watch / >10min breach
              const status = slaStatus(att.avgSupportTime || 0, { ok: 300, warn: 600 });
              const dotColor =
                status === "healthy"
                  ? tokens.healthy
                  : status === "watch"
                  ? tokens.watch
                  : status === "breach"
                  ? tokens.breach
                  : tokens.textMuted;
              const breakdown = Array.isArray(att.breakdown) ? att.breakdown : [];
              const hasBreakdown = breakdown.length > 1;
              const tooltipText = hasBreakdown
                ? "Detalhamento por empresa:\n" +
                  breakdown
                    .map(b => `${b.companyName}: ${fmtNumberPtBR(b.tickets)} tickets · ART ${secToHms(b.avgSupportTime)}`)
                    .join("\n")
                : `${att.name}: ${fmtNumberPtBR(att.tickets || 0)} tickets · ART ${secToHms(att.avgSupportTime)}`;

              const nameCell = (
                <span
                  className={classes.tableCellName}
                  style={{
                    cursor: hasBreakdown ? "help" : "default",
                    textDecoration: hasBreakdown ? "underline dotted" : "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6
                  }}
                  title={att.name}
                >
                  {att.name}
                  {att.isAI && (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        padding: "1px 5px",
                        borderRadius: 4,
                        background: tokens.accentSoft,
                        color: tokens.accent,
                        letterSpacing: "0.05em"
                      }}
                    >
                      IA
                    </span>
                  )}
                </span>
              );

              return (
                <div key={i} className={classes.tableRow}>
                  <span
                    className={classes.statusDot}
                    style={{ background: dotColor }}
                  />
                  {hasBreakdown ? (
                    <GlassTooltip title={tooltipText} arrow placement="top">
                      {nameCell}
                    </GlassTooltip>
                  ) : (
                    nameCell
                  )}
                  <StatusBadge status={status} tokens={tokens} />
                  <BarInCell
                    value={Number(att.tickets) || 0}
                    max={maxTickets}
                    color={tokens.accent}
                    bg={tokens.surfaceBg}
                    height={5}
                    textColor={tokens.textPrimary}
                    label={fmtNumberPtBR(att.tickets || 0)}
                  />
                  <span
                    className={classes.tableCellNum}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <MessageSquare size={11} style={{ color: tokens.textMuted }} />
                    {fmtNumberPtBR(att.messages || 0)}
                  </span>
                  <span className={classes.tableCellNum}>
                    {secToHms(att.avgSupportTime)}
                  </span>
                  <span className={classes.tableCellNum}>
                    {secToHms(att.avgWaitTime)}
                  </span>
                  <span className={classes.tableCellNum}>
                    {fmtNumberPtBR(att.ticketsToday != null ? att.ticketsToday : 0)}
                  </span>
                  <button
                    className={classes.actionBtn}
                    style={{ padding: 6 }}
                    title="Ver detalhamento por empresa"
                    onClick={() => setDetailRow(att)}
                  >
                    <Eye size={14} />
                  </button>
                </div>
              );
            })
          )}
          {/* Paginação */}
          {filteredAttendants.length > PAGE_SIZE && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 16px",
                borderTop: "1px solid " + tokens.cardBorder,
                background: tokens.cardBgStrong,
                fontSize: 12,
                color: tokens.textMuted
              }}
            >
              <span>
                Mostrando{" "}
                <strong style={{ color: tokens.textPrimary }}>
                  {(safePage - 1) * PAGE_SIZE + 1}
                  –
                  {Math.min(safePage * PAGE_SIZE, filteredAttendants.length)}
                </strong>{" "}
                de{" "}
                <strong style={{ color: tokens.textPrimary }}>
                  {filteredAttendants.length}
                </strong>{" "}
                atendentes
              </span>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <button
                  className={classes.actionBtn}
                  style={{ padding: 4, opacity: safePage <= 1 ? 0.35 : 1 }}
                  disabled={safePage <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  title="Anterior"
                >
                  <ChevronLeft size={14} />
                </button>
                <span style={{ minWidth: 60, textAlign: "center" }}>
                  Pág. {safePage} / {totalPages}
                </span>
                <button
                  className={classes.actionBtn}
                  style={{ padding: 4, opacity: safePage >= totalPages ? 0.35 : 1 }}
                  disabled={safePage >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  title="Próxima"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ───── Z4 CHARTS STRIP ───── */}
        {charts && (
          <>
            <div className={classes.chartsHeader}>
              <span className={classes.chartsTitle}>
                Histórico · últimos 12 meses
              </span>
              <span className={classes.chartsSub}>
                <Activity size={11} style={{ verticalAlign: "middle" }} /> dados
                reais
              </span>
            </div>
            <div className={classes.chartsGrid}>
              <ChartCardItem
                label="Casos sem resposta"
                series={charts.openNoReply}
                isTime={false}
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="Maior tempo sem resposta"
                series={charts.longestNoReplyAttendantSec}
                isTime
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="ART médio · atendente"
                series={charts.avgFirstReplyAttendantSec}
                isTime
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="Maior tempo de 1ª resposta"
                series={charts.longestFirstReplySec}
                isTime
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="ART médio · cliente"
                series={charts.avgFirstReplyClientSec}
                isTime
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="Total de casos em aberto"
                series={charts.totalOpen}
                isTime={false}
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="Aguardando resposta do cliente"
                series={charts.waitingClient}
                isTime={false}
                tokens={tokens}
                classes={classes}
              />
              <ChartCardItem
                label="Duração média da interação"
                series={charts.avgInteractionSec}
                isTime
                tokens={tokens}
                classes={classes}
              />
            </div>

            {charts.totalDaily && (
              <>
                <div className={classes.chartsHeader} style={{ marginTop: 24 }}>
                  <span className={classes.chartsTitle}>
                    Atendimentos · últimos 30 dias
                  </span>
                </div>
                <div className={classes.chartCard} style={{ minHeight: 240 }}>
                  <div className={classes.chartHeaderRow}>
                    <span className={classes.chartTitle}>Tickets por dia</span>
                    <span className={classes.chartValue}>
                      {fmtNumberPtBR(
                        charts.totalDaily[charts.totalDaily.length - 1]?.value || 0
                      )}
                    </span>
                  </div>
                  <div className={classes.chartBody} style={{ minHeight: 160 }}>
                    <Sparkline
                      data={charts.totalDaily}
                      color={tokens.accent}
                      width={1200}
                      height={160}
                      idSuffix="daily"
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* ───── Modal detalhamento por empresa ───── */}
      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            background: tokens.cardBg,
            border: "1px solid " + tokens.cardBorder,
            borderRadius: 14,
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)"
          }
        }}
      >
        {detailRow && (
          <div style={{ padding: 0 }}>
            {/* Header */}
            <div
              style={{
                padding: "18px 24px",
                borderBottom: "1px solid " + tokens.cardBorder,
                background: tokens.cardBgStrong,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: tokens.textMuted,
                    marginBottom: 4
                  }}
                >
                  Detalhamento · período {period}
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: tokens.textPrimary,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8
                  }}
                >
                  {detailRow.name}
                  {detailRow.isAI && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: tokens.accentSoft,
                        color: tokens.accent,
                        letterSpacing: "0.05em"
                      }}
                    >
                      IA
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: tokens.textMuted,
                    marginTop: 4
                  }}
                >
                  Presente em{" "}
                  <strong style={{ color: tokens.textSecondary }}>
                    {detailRow.totalCompanies ||
                      (detailRow.breakdown || []).length}{" "}
                    empresa(s)
                  </strong>
                </div>
              </div>
              <button
                className={classes.actionBtn}
                style={{ padding: 6 }}
                onClick={() => setDetailRow(null)}
                title="Fechar"
              >
                <XIcon size={16} />
              </button>
            </div>

            {/* Totais agregados */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 12,
                padding: "16px 24px",
                borderBottom: "1px solid " + tokens.cardBorder
              }}
            >
              {[
                {
                  label: "Atendimentos",
                  value: fmtNumberPtBR(detailRow.tickets || 0)
                },
                {
                  label: "Mensagens",
                  value: fmtNumberPtBR(detailRow.messages || 0)
                },
                {
                  label: "ART médio",
                  value: secToHms(detailRow.avgSupportTime || 0)
                },
                {
                  label: "Tempo espera",
                  value: secToHms(detailRow.avgWaitTime || 0)
                }
              ].map((s, idx) => (
                <div
                  key={idx}
                  style={{
                    background: tokens.surfaceBg,
                    border: "1px solid " + tokens.cardBorder,
                    borderRadius: 10,
                    padding: "10px 12px"
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: tokens.textMuted,
                      marginBottom: 4
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: tokens.textPrimary,
                      fontVariantNumeric: "tabular-nums"
                    }}
                  >
                    {s.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Tabela breakdown por empresa */}
            <div style={{ padding: "16px 24px 24px" }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: tokens.textMuted,
                  marginBottom: 10
                }}
              >
                Por empresa
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(180px,1fr) 100px 100px 110px 110px",
                  gap: 10,
                  padding: "8px 12px",
                  background: tokens.cardBgStrong,
                  borderRadius: 8,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: tokens.textMuted
                }}
              >
                <span>Empresa</span>
                <span style={{ textAlign: "right" }}>Atend.</span>
                <span style={{ textAlign: "right" }}>Mensagens</span>
                <span style={{ textAlign: "right" }}>ART</span>
                <span style={{ textAlign: "right" }}>Espera</span>
              </div>
              {(detailRow.breakdown || []).length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    fontSize: 13,
                    color: tokens.textMuted,
                    fontStyle: "italic",
                    textAlign: "center"
                  }}
                >
                  Sem dados de breakdown.
                </div>
              ) : (
                (detailRow.breakdown || []).map((b, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "minmax(180px,1fr) 100px 100px 110px 110px",
                      gap: 10,
                      padding: "10px 12px",
                      borderBottom: "1px solid " + tokens.cardBorder,
                      alignItems: "center"
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: tokens.textPrimary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                      title={b.companyName}
                    >
                      {b.companyName}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        textAlign: "right",
                        color: tokens.textPrimary,
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums"
                      }}
                    >
                      {fmtNumberPtBR(b.tickets || 0)}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        textAlign: "right",
                        color: tokens.textSecondary,
                        fontVariantNumeric: "tabular-nums"
                      }}
                    >
                      {fmtNumberPtBR(b.messages || 0)}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        textAlign: "right",
                        color: tokens.textSecondary,
                        fontVariantNumeric: "tabular-nums"
                      }}
                    >
                      {secToHms(b.avgSupportTime || 0)}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        textAlign: "right",
                        color: tokens.textSecondary,
                        fontVariantNumeric: "tabular-nums"
                      }}
                    >
                      {secToHms(b.avgWaitTime || 0)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Sub-componente local: card de gráfico histórico
// ─────────────────────────────────────────────────────────────────────
const ChartCardItem = ({ label, series, isTime, tokens, classes }) => {
  if (!series || series.length === 0) {
    return (
      <div className={classes.chartCard}>
        <div className={classes.chartHeaderRow}>
          <span className={classes.chartTitle}>{label}</span>
        </div>
        <div className={classes.emptyState} style={{ padding: 32 }}>
          Sem dados.
        </div>
      </div>
    );
  }
  const lastVal = series[series.length - 1]?.value ?? 0;
  const fmt = isTime
    ? v => {
        if (v == null || v <= 0) return "—";
        const s = Math.round(v);
        if (s < 60) return s + "s";
        const m = Math.floor(s / 60);
        if (m < 60) return m + "min";
        const h = Math.floor(m / 60);
        return h + "h" + (m - h * 60 ? (m - h * 60) + "min" : "");
      }
    : v => Number(v || 0).toLocaleString("pt-BR");
  return (
    <div className={classes.chartCard}>
      <div className={classes.chartHeaderRow}>
        <span className={classes.chartTitle}>{label}</span>
        <span className={classes.chartValue}>{fmt(lastVal)}</span>
      </div>
      <div className={classes.chartBody}>
        <Sparkline
          data={series}
          color={tokens.accent}
          width={600}
          height={120}
          idSuffix={label}
        />
      </div>
    </div>
  );
};

export default MultiUnidadesV2;
